import { Subject } from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List } from 'immutable';
import {
  DIALOG_ANIMATE_TIME,
  DialogActionsEnum,
  IDialogTrigger,
} from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import {
  DocumentTypesEnum,
  ICopyDocumentOptions,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { CopyDocumentDialogBuilder } from './copy-document-dialog.builder';
import { filter, takeUntil } from 'rxjs/operators';
import {
  DIALOG_TYPE,
  ICopyDocumentDialogData,
} from './copy-document-dialog.data';

@Component({
  selector: 'app-copy-document-dialog',
  templateUrl: './copy-document-dialog.component.html',
  styleUrls: ['./copy-document-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [CopyDocumentDialogBuilder],
})
export class CopyDocumentDialogComponent implements OnInit, OnDestroy {
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() document: Document;
  @Input() taxes: List<Tax>;
  @Input() disableDocumentTypes: List<DocumentTypesEnum>;

  @Output()
  onCopyDocument: EventEmitter<ICopyDocumentOptions> = new EventEmitter<ICopyDocumentOptions>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _emptyFormValue: any;

  form: FormGroup;
  formKeys: any;
  copyAsOptions: List<ISelectOption>;
  headerLabel: string;
  dialogType: string;
  copyAction: ITrackingAction;
  cancelAction: ITrackingAction;
  trackingObject: ITrackingObject;
  showCopyTaxesOption: boolean;

  constructor(
    private _copyDocumentDialogBuilder: CopyDocumentDialogBuilder,
    private _dialogService: DialogService,
    private _translateService: TranslateService,
    private _utilsService: UtilsService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this.formKeys = this._copyDocumentDialogBuilder.FORM_KEYS;
    this.dialogType = DIALOG_TYPE;
    this.trackingObject = this._trackingObjectBuilder.buildDialog(
      TrackingDialogNamesEnum.copy_document
    );
    this.cancelAction = this._trackingActionBuilder.buildButtonTapped(
      `cancel`,
      this.trackingScreenName
    );

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ action, type }: IDialogTrigger) =>
            type === this.dialogType && action !== DialogActionsEnum.close
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(({ data }: IDialogTrigger) => {
        const {
          document,
          disableDocumentTypes,
        }: ICopyDocumentDialogData = data;

        this.document = document || this.document;
        this.disableDocumentTypes =
          disableDocumentTypes || this.disableDocumentTypes;
        this.showCopyTaxesOption = this._utilsService.checkTaxConflicts(
          this.taxes,
          this.document
        );

        const documentType: DocumentTypesEnum = this.document.documentType;

        this.form = this._copyDocumentDialogBuilder.buildForm(documentType);
        this.headerLabel = this._translateService.instant(
          `DIALOG.COPY_DOCUMENT.${documentType}`.toUpperCase()
        );
        this.copyAction = this._trackingActionBuilder.buildButtonTapped(
          `copy_${documentType}`,
          this.trackingScreenName
        );
        this.copyAsOptions = this._copyDocumentDialogBuilder.buildSelectOptions(
          this.disableDocumentTypes || List()
        );

        this._emptyFormValue = this.form.value;
      });
  }

  onCopy() {
    this.onCopyDocument.emit(
      this._copyDocumentDialogBuilder.buildCopyOptions(
        fromJS(this.form.value),
        this.document
      )
    );
  }

  onCloseDialog() {
    // wait until the dialog is closed all the way before resetting
    // { emitEvent: false } to prevent tracking events from firing on form field changes
    setTimeout(
      () => this.form.reset(this._emptyFormValue, { emitEvent: false }),
      DIALOG_ANIMATE_TIME
    );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
